import {
  DetailsRow,
  IColumn,
  IDetailsListProps,
  IDetailsRowStyles,
  IDragDropContext,
  IDragDropEvents,
  mergeStyles,
} from '@fluentui/react';
import { IPScrollablePane, IPText } from 'baseComponents';
import { IPDetailsList } from 'baseComponents/DetailsList/IPDetailsList';
import { IPScrollbarVisibility } from 'baseComponents/ScrollablePane/IPScrollablePane';
import { IPStack } from 'baseComponents/Stack/IPStack';
import EditNumberFieldCallOut from 'components/CallOuts/EditNumberFieldCallOut';
import EditTextFieldCallOut from 'components/CallOuts/EditTextFieldCallOut';
import { AddComboBoxListItem } from 'components/ComboBoxItemCreator/AddComboBoxListItem';
import { ComboBoxCalloutInformation, ComboBoxListItemRow } from 'components/ComboBoxItemCreator/ComboBoxListItemRow';
import { globalStackItemStylesPaddingSceneScroll, globalStackTokensGapSmall } from 'globalStyles';
import { ComboBoxItem, ComboBoxItemList } from 'models/combobox/ComboBoxItem';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IPSelectionMode } from "baseComponents/Utils/Selection";

interface IComboBoxItemCreator {
  allowEdit?: boolean;
  singleLineEdit?: boolean;
  itemList?: ComboBoxItemList;
  onUpdate: (newItemList: ComboBoxItemList) => void;
}

const ComboBoxItemCreator = (props: IComboBoxItemCreator) => {
  const { t } = useTranslation(['translation', 'comboBoxItem']);
  const [showIcon, setShowIcon] = useState<number | undefined>(undefined);
  const [_draggedItem, _setDraggedItem] = useState<ComboBoxItem | undefined>(undefined);
  const [itemDescription, setItemDescription] = useState<string>('');
  const [editNameCallOutInfo, setEditNameCalloutInfo] = useState<ComboBoxCalloutInformation | undefined>(undefined);
  const [editKeyCallOutInfo, setEditKeyCalloutInfo] = useState<ComboBoxCalloutInformation | undefined>(undefined);
  const [statusButtonIds, setStatusButtonIds] = useState<string[]>([]);
  const [editButtonIds, setEditButtonIds] = useState<string[]>([]);

  useEffect(() => {
    if (props.itemList) {
      const listItems = props.itemList.items;

      setStatusButtonIds(
        listItems.map((_itm) => {
          return 'RandomStatusButton' + Math.random().toString(36).substring(7);
        }),
      );

      if (!editNameCallOutInfo && !editKeyCallOutInfo) {
        setEditButtonIds(
          listItems.map((_itm) => {
            return 'RandomEditButton' + Math.random().toString(36).substring(7);
          }),
        );
      }
    } else {
      setStatusButtonIds([]);
      setEditButtonIds([]);
    }
  }, [editNameCallOutInfo, editKeyCallOutInfo, props.itemList]);

  const onDelete = (item: ComboBoxItem) => {
    if (!props.itemList) return;

    const _newComboBoxList = props.itemList.clone();
    _newComboBoxList.items = _newComboBoxList.items.filter((itm) => {
      return !itm.isEqual(item);
    });

    setShowIcon(undefined);
    props.onUpdate(_newComboBoxList);
  };

  const onAddNewComboBoxListItem = () => {
    if (itemDescription === '' || !props.itemList) return;

    const _newComboBoxList = props.itemList.clone();
    const _comboBoxListItem = new ComboBoxItem();
    _comboBoxListItem.key = props.itemList.getMaxKey() + 1;
    _comboBoxListItem.description = itemDescription;
    _newComboBoxList.items.push(_comboBoxListItem);
    props.onUpdate(_newComboBoxList);
    setItemDescription('');
  };

  const updateKey = (item: ComboBoxItem, key: number) => {
    if (!props.itemList) return;
    const _newComboBoxList = props.itemList.clone();
    const idx = _newComboBoxList.items.findIndex((i) => i.id === item?.id);
    if (idx >= 0) {
      _newComboBoxList.items[idx].key = key;
    }
    props.onUpdate(_newComboBoxList);
  };

  const updateName = (item: ComboBoxItem, description: string) => {
    if (!props.itemList) return;
    const _newComboBoxList = props.itemList.clone();
    const idx = _newComboBoxList.items.findIndex((i) => i.id === item?.id);
    if (idx >= 0) {
      _newComboBoxList.items[idx].description = description;
    }
    props.onUpdate(_newComboBoxList);
  };

  const updateDefault = (item: ComboBoxItem | undefined) => {
    if (!props.itemList) return;
    const _newComboBoxList = props.itemList.clone();
    _newComboBoxList.default = item;
    setShowIcon(undefined);
    props.onUpdate(_newComboBoxList);
  };

  const columns: IColumn[] = [
    {
      key: 'row',
      name: 'row',
      minWidth: 150,
      isMultiline: true,
      onRender: (item?: ComboBoxItem, index?: number, column?: IColumn) => {
        if (!item || index === undefined) return;

        return (
          <ComboBoxListItemRow
            allowEdit={props.allowEdit ? true : false}
            index={index}
            setShowIcon={setShowIcon}
            statusButtonId={statusButtonIds[index]}
            editButtonId={editButtonIds[index]}
            showIcon={showIcon}
            item={item}
            onDelete={onDelete}
            callOutInfo={editNameCallOutInfo}
            setCallOutNameInfo={setEditNameCalloutInfo}
            setCallOutKeyInfo={setEditKeyCalloutInfo}
            setDefault={updateDefault}
            isDefault={props.itemList?.default?.id === item.id}
          />
        );
      },
    },
  ];

  const _insertBeforeItem = (item?: ComboBoxItem) => {
    if (!item || !props.itemList || !_draggedItem) return;

    const insertIndex = props.itemList.items.indexOf(item);
    const newItems = props.itemList.items.filter((itm) => {
      return !itm.isEqual(_draggedItem);
    });

    newItems.splice(insertIndex, 0, _draggedItem);
    const _newComboBoxList = props.itemList.clone();
    _newComboBoxList.items = newItems;

    props.onUpdate(_newComboBoxList);
  };

  const dragAndDropEventHandler: IDragDropEvents = {
    canDrop: (dropContext?: IDragDropContext, dragContext?: IDragDropContext) => {
      return true;
    },
    canDrag: (item?: ComboBoxItem) => {
      if (props.allowEdit) return true;

      return false;
    },
    onDragEnter: (item?: ComboBoxItem, event?: DragEvent) => {
      // return string is the css classes that will be added to the entering element.
      return mergeStyles({
        border: '2',
        borderBottom: 2,
        borderColor: 'blue',
      });
    },
    onDragLeave: (item?: ComboBoxItem, event?: DragEvent) => {
      return;
    },
    onDrop: (item?: ComboBoxItem, event?: DragEvent) => {
      if (_draggedItem) {
        _insertBeforeItem(item);
      }
    },
    onDragStart: (item?: ComboBoxItem, itemIndex?: number, selectedItems?: ComboBoxItem[], event?: MouseEvent) => {
      _setDraggedItem(item);
    },
    onDragEnd: (item?: ComboBoxItem, event?: DragEvent) => {
      _setDraggedItem(undefined);
    },
  };

  const onRenderRow: IDetailsListProps['onRenderRow'] = (props) => {
    const customStyles: Partial<IDetailsRowStyles> = {};
    if (props) {
      if (props.cellStyleProps) {
        props.cellStyleProps.cellLeftPadding = 5;
      }

      return <DetailsRow {...props} styles={customStyles} />;
    }

    return null;
  };

  return (
    <IPStack verticalFill tokens={globalStackTokensGapSmall}>
      {props.itemList?.items.length === 0 && (
        <IPStack grow horizontalAlign="center" verticalAlign="center">
          <IPText>{t('comboBoxItem:ComboBoxList.NoData')}</IPText>
        </IPStack>
      )}
      {props.itemList && props.itemList.items.length > 0 && (
        <IPStack.Item grow styles={globalStackItemStylesPaddingSceneScroll}>
          <IPScrollablePane scrollbarVisibility={IPScrollbarVisibility.auto}>
            <IPDetailsList
              compact
              items={props.itemList?.items ?? []}
              columns={columns}
              selectionMode={IPSelectionMode.none}
              isHeaderVisible={false}
              dragDropEvents={props.allowEdit ? dragAndDropEventHandler : undefined}
              onRenderRow={onRenderRow}
            />
          </IPScrollablePane>
        </IPStack.Item>
      )}
      {props.allowEdit && (
        <IPStack.Item style={{ paddingLeft: '5px' }}>
          <AddComboBoxListItem
            itemDescription={itemDescription}
            setItemDescription={setItemDescription}
            onAddListItem={onAddNewComboBoxListItem}
            allowEdit={props.allowEdit ? true : false}
            singleLineEdit={props.singleLineEdit}
          />
        </IPStack.Item>
      )}
      <IPStack.Item>
        {editNameCallOutInfo && (
          <EditTextFieldCallOut
            required={true}
            isVisible={editNameCallOutInfo !== undefined}
            targetId={editNameCallOutInfo.buttonId}
            value={editNameCallOutInfo.item.description}
            onClose={() => {
              setEditNameCalloutInfo(undefined);
            }}
            onUpdate={(newText: string) => {
              updateName(editNameCallOutInfo.item, newText);
            }}
          />
        )}
      </IPStack.Item>
      <IPStack.Item>
        {editKeyCallOutInfo && (
          <EditNumberFieldCallOut
            isVisible={editKeyCallOutInfo !== undefined}
            targetId={editKeyCallOutInfo.buttonId}
            value={editKeyCallOutInfo.item.key}
            onClose={() => {
              setEditKeyCalloutInfo(undefined);
            }}
            onUpdate={(newVal: number): string | undefined => {
              if (props.itemList?.items.find((i) => i.key === newVal && i.id !== editKeyCallOutInfo.item.id)) {
                return t('comboBoxItem:ComboBoxList.Errors.KeyNotUnique');
              } else {
                updateKey(editKeyCallOutInfo.item, newVal);

                return undefined;
              }
            }}
          />
        )}
      </IPStack.Item>
    </IPStack>
  );
};
export default ComboBoxItemCreator;
